import React, { useEffect, useState } from 'react'
import api from '../../api/api'
import useStore from 'state/knovStore'
import TeamLogo from 'components/shared/icons/TeamLogo'
import styles from 'components/quests/perms-options.module.scss'
import perms from 'lib/perms'
import cn from 'classnames'
import PublicIcon from 'components/shared/icons/PublicIcon'
import PrivateIcon from 'components/shared/icons/PrivateIcon'
import QuestOptionsHeader from 'components/quests/QuestOptionsHeader'
import { userHasFeature } from 'refactor/hooks/useUserHasFeature'

function getPermsOptionTestId(opt) {
    if (opt.type === 'team') return `perms-option-team-${opt.value?.id}`
    return `perms-option-${String(opt.value || '').toLowerCase()}`
}

export default function PermsOptions(props) {
    const quest = props.quest
    const amOwner = perms.amQuestOwner(quest)
    const userStreams = useStore(state => state.userStreams) || []

    // Drafts can alternatively publish into one of the user's recent threads
    // when publish tabs are enabled. Published quests only ever re-home between streams.
    const showThreadTab = !!quest.is_draft && userHasFeature('publish_tabs')
    const [activeTab, setActiveTab] = useState(
        showThreadTab && quest.destination_quest_id ? 'threads' : 'streams',
    )
    const [recentThreads, setRecentThreads] = useState(null)
    const [recentThreadsError, setRecentThreadsError] = useState(false)

    useEffect(() => {
        if (!showThreadTab || activeTab !== 'threads' || recentThreads !== null) return
        let cancelled = false
        api.fetchRecentThreads()
            .then(threads => {
                if (!cancelled) setRecentThreads(threads)
            })
            .catch(error => {
                console.error('Failed to fetch recent threads', error)
                if (!cancelled) setRecentThreadsError(true)
            })
        return () => {
            cancelled = true
        }
    }, [activeTab, showThreadTab, recentThreads])

    const publishQuest = async perms => {
        props.close()
        if (!amOwner) return

        const { updateQuest } = useStore.getState()

        if (quest.is_draft) {
            const permedQuest = {
                ...quest,
                updated_at: Date.now(),
                // Picking a stream always supersedes a previously picked thread destination.
                destination_quest_id: null,
                destination_quest_preview: null,
                // An explicit pick is never the join-to-post fallback.
                join_gate_team_id: null,
                ...perms,
            }
            Object.assign(quest, permedQuest)
            updateQuest(permedQuest)
            return
        }

        const serverPerms = { ...perms }
        delete serverPerms.team

        try {
            const updatedQuest = await api.updateQuestPerms(props.quest.id, serverPerms)
            if (updatedQuest) {
                Object.assign(quest, updatedQuest)
                updateQuest(updatedQuest)
            }
        } catch (error) {
            console.error('Failed to update quest permissions', error)
        }
    }

    // Point the draft at an existing thread: publishing then appends the composed
    // post to that thread instead of creating a new one.
    const publishQuestToThread = thread => {
        props.close()
        if (!amOwner || !quest.is_draft) return

        const { updateQuest } = useStore.getState()
        const destQuest = {
            ...quest,
            updated_at: Date.now(),
            destination_quest_id: thread.id,
            destination_quest_preview: thread.content || '',
        }
        Object.assign(quest, destQuest)
        updateQuest(destQuest)
    }

    // Access setters.
    const publishQuestToTeam = async team => {
        publishQuest({
            public: false,
            private: false,
            team_id: team.id,
            team: team,
        })
    }

    const setQuestPublic = async () => {
        publishQuest({
            public: true,
            private: false,
            team_id: null,
            team: null,
        })
    }

    const setQuestPrivate = async () => {
        publishQuest({
            public: false,
            private: true,
            team_id: null,
            team: null,
        })
    }

    const setQuestLink = async () => {
        publishQuest({
            public: false,
            private: false,
            team_id: null,
            team: null,
        })
    }

    const defaultOptions = [
        {
            type: 'publicPrivateLink',
            name: 'Private',
            value: 'PRIVATE',
            desc: 'Publish thread to Private channel.',
            tip: 'Private threads can be viewed only by you and anyone you @mention.',
            logo: <PrivateIcon contextStyles={styles.privateIcon} />,
            callback: () => setQuestPrivate(),
        },
        {
            type: 'publicPrivateLink',
            name: 'Public',
            value: 'PUBLIC',
            desc: 'Publish thread publicly.',
            tip: 'Public threads can be viewed by anyone.',
            logo: <PublicIcon contextStyles={styles.publicIcon} />,
            callback: () => setQuestPublic(),
        },
        {
            type: 'publicPrivateLink',
            name: 'Link',
            value: 'LINK',
            desc: 'Publish thread to Link channel.',
            tip: 'Link threads can be viewed by anyone with the thread url.',
            logo: (
                <div className={styles.purpleIcon}>
                    <i className="fa fa-link" />
                </div>
            ),
            callback: () => setQuestLink(),
        },
    ]

    const visibleTeams = userStreams.filter((team, index, teams) => {
        if (!team) return false

        const membership = team?.users_teams
        let isVisible = true

        if (membership) {
            if (Array.isArray(membership)) {
                const currentUserId = gon.currentUser?.id
                const currentMembership =
                    (currentUserId &&
                        membership.find(usersTeam => usersTeam?.user_id === currentUserId)) ||
                    membership[0]
                isVisible = currentMembership?.hidden !== true
            } else {
                isVisible = membership.hidden !== true
            }
        }

        if (!isVisible) return false

        return teams.findIndex(candidate => candidate?.id === team.id) === index
    })

    const availableTeams = quest.team
        ? visibleTeams.filter(team => team.id !== quest.team.id)
        : visibleTeams

    const teamOptions =
        (availableTeams || []).map(team => ({
            type: 'team',
            name: team.name,
            value: team,
            desc: `Publish quest to ${team.name} channel.`,
            logo: team.team_image ? team.team_image.url : null,
            callback: () => publishQuestToTeam(team),
        })) || []

    const selectedDefaultValue = (() => {
        if (props.quest.team) return null
        if (props.quest.public && !props.quest.private) return 'PUBLIC'
        if (!props.quest.public && !props.quest.private && !props.quest.team_id) return 'LINK'
        return 'PRIVATE'
    })()

    const selectedTeamId = props.quest.team?.id || props.quest.team_id || null

    // A picked thread destination overrides the draft's stream perms until cleared.
    const destinationSelected = showThreadTab && !!quest.destination_quest_id

    const streamOptionsView = (
        <>
            {defaultOptions
                //.filter(opt => opt.value !== questOpt.value)
                .map((opt, ix) => {
                    return (
                        <div key={ix}>
                            <PermsOption
                                opt={opt}
                                isSelected={
                                    !destinationSelected && opt.value === selectedDefaultValue
                                }
                            />
                        </div>
                    )
                })}

            {teamOptions.length > 0 && (
                <div className={styles.separatorContainer}>
                    <div className={styles.separator}></div>
                </div>
            )}

            {teamOptions
                //.filter(opt => opt.value !== questOpt.value)
                .map((opt, ix) => {
                    return (
                        <div key={ix}>
                            <PermsOption
                                opt={opt}
                                isSelected={!destinationSelected && opt.value.id === selectedTeamId}
                            />
                        </div>
                    )
                })}
        </>
    )

    const threadOptionsView = (() => {
        if (recentThreadsError)
            return <div className={styles.threadListStatus}>Couldn't load recent threads.</div>
        if (recentThreads === null)
            return <div className={styles.threadListStatus}>Loading recent threads...</div>
        if (recentThreads.length === 0)
            return <div className={styles.threadListStatus}>No recent threads yet.</div>

        return recentThreads.map(thread => {
            const isSelected =
                destinationSelected && String(quest.destination_quest_id) === String(thread.id)
            return (
                <div key={thread.id}>
                    <div
                        className={cn(styles.permsOptionComp, isSelected && styles.selected)}
                        data-testid={`perms-option-thread-${thread.id}`}
                        data-selected={isSelected ? 'true' : 'false'}
                        onClick={() => publishQuestToThread(thread)}
                    >
                        <span className={styles.permsIcon}>
                            <div className={styles.threadIcon}>
                                <i className="fa fa-comments" />
                            </div>
                        </span>

                        <span className={styles.threadPreview}>
                            {thread.content || 'Untitled thread'}
                        </span>
                    </div>
                </div>
            )
        })
    })()

    // render
    return (
        <div className={styles.permsOptionsComp} data-testid="perms-options-modal">
            {props.showHeader && (
                <QuestOptionsHeader
                    title={
                        showThreadTab && activeTab === 'threads'
                            ? 'Publish to Thread'
                            : 'Publish to Channel'
                    }
                    close={props.close}
                    className={styles.permsOptionsHeader}
                />
            )}

            {showThreadTab && (
                <div className={styles.tabsRow}>
                    <div
                        className={cn(styles.tab, activeTab === 'streams' && styles.activeTab)}
                        data-testid="perms-tab-streams"
                        onClick={() => setActiveTab('streams')}
                    >
                        Channels
                    </div>
                    <div
                        className={cn(styles.tab, activeTab === 'threads' && styles.activeTab)}
                        data-testid="perms-tab-threads"
                        onClick={() => setActiveTab('threads')}
                    >
                        Threads
                    </div>
                </div>
            )}

            <div className={styles.permsOptionsContainer}>
                <div className={styles.permsOptions}>
                    {showThreadTab && activeTab === 'threads'
                        ? threadOptionsView
                        : streamOptionsView}
                </div>
            </div>
        </div>
    )
}

function PermsOption(props) {
    const opt = props.opt
    const team = opt.value

    const iconStyles = opt?.type === 'publicPrivateLink' ? styles.purpleIcon : null
    const selectedStyles = props.isSelected ? styles.selected : null

    return (
        <div
            className={cn(styles.permsOptionComp, selectedStyles)}
            data-testid={getPermsOptionTestId(opt)}
            data-selected={props.isSelected ? 'true' : 'false'}
            onClick={opt.callback}
        >
            <span className={styles.permsIcon}>
                {opt.type === 'publicPrivateLink' ? (
                    opt.logo
                ) : (
                    <TeamLogo team={team} contextStyles={cn(styles.permsIcon, iconStyles)} />
                )}
            </span>

            <span className={styles.name}>{opt.name}</span>

            <span className={styles.description}>{opt.desc}</span>
        </div>
    )
}
